import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import type { DiscussionSpace } from '@/server/modules/discussions/db/schema'
import type {
  CreateSpaceInput,
  UpdateSpaceInput,
} from '@/shared/schemas/discussion.schema'
import { buildQueryString, fetchJson, fetchMutation } from '@/client/lib/api-helpers'
import type { ApiDeleteResponse } from '@/client/lib/api-types'

/**
 * TanStack Query hooks for discussion spaces
 */

const API_BASE = '/api/discussions/spaces'

// Query keys for cache management
export const SPACES_KEYS = {
  all: ['discussionSpaces'] as const,
  lists: () => [...SPACES_KEYS.all, 'list'] as const,
  list: (filters?: { search?: string }) => [...SPACES_KEYS.lists(), filters] as const,
  details: () => [...SPACES_KEYS.all, 'detail'] as const,
  detail: (slug: string) => [...SPACES_KEYS.details(), slug] as const,
}

// API response types with parsed fields
// Using Omit to override JSON string fields with parsed types
export interface SpaceWithCreator extends Omit<DiscussionSpace, 'tags' | 'settings'> {
  tags: string[]
  settings: Record<string, unknown>
  creator: {
    id: string
    name: string
    image: string | null
  }
}

interface SpacesResponse {
  spaces: SpaceWithCreator[]
  count: number
}

interface SpaceResponse {
  space: SpaceWithCreator
}

/**
 * Fetch all discussion spaces
 */
export function useSpaces(filters?: { search?: string }) {
  return useQuery({
    queryKey: SPACES_KEYS.list(filters),
    queryFn: async (): Promise<SpaceWithCreator[]> => {
      const data = await fetchJson<SpacesResponse>(
        `${API_BASE}${buildQueryString(filters)}`
      )
      return data.spaces
    },
  })
}

/**
 * Fetch a single space by slug
 */
export function useSpace(slug: string) {
  return useQuery({
    queryKey: SPACES_KEYS.detail(slug),
    queryFn: async (): Promise<SpaceWithCreator> => {
      const data = await fetchJson<SpaceResponse>(`${API_BASE}/${slug}`)
      return data.space
    },
    enabled: !!slug,
  })
}

/**
 * Create a new space
 */
export function useCreateSpace() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (input: CreateSpaceInput): Promise<SpaceWithCreator> => {
      const data = await fetchMutation<SpaceResponse>(API_BASE, 'POST', input)
      return data.space
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: SPACES_KEYS.lists() })
    },
  })
}

/**
 * Update an existing space
 */
export function useUpdateSpace() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({
      id,
      data,
    }: {
      id: string
      data: UpdateSpaceInput
    }): Promise<SpaceWithCreator> => {
      const result = await fetchMutation<SpaceResponse>(
        `${API_BASE}/${id}`,
        'PUT',
        data
      )
      return result.space
    },
    onSuccess: (space) => {
      queryClient.invalidateQueries({ queryKey: SPACES_KEYS.lists() })
      queryClient.invalidateQueries({ queryKey: SPACES_KEYS.detail(space.slug) })
    },
  })
}

/**
 * Delete a space
 */
export function useDeleteSpace() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (id: string): Promise<void> => {
      const data = await fetchMutation<ApiDeleteResponse>(
        `${API_BASE}/${id}`,
        'DELETE'
      )
      if (!data.success) {
        throw new Error('Delete operation failed')
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: SPACES_KEYS.lists() })
    },
  })
}
